import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Heart, LogOut, FileDown, Check, X, ShoppingBag, History, Search, Sparkles, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { WhatsAppFab } from "@/components/WhatsAppFab";
import { ConciergeChat } from "@/components/ConciergeChat";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const KARAT_OPTIONS = ["22kt", "18kt", "14kt", "9kt", "925 SS"];
const IMG_PLACEHOLDER = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100'><rect width='100' height='100' fill='%23F2F2F3'/></svg>";

export default function BuyerCatalog() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("All");
  const [activeType, setActiveType] = useState("All");
  const [activeSub, setActiveSub] = useState("All");
  const [sort, setSort] = useState("latest");
  const [taxonomy, setTaxonomy] = useState({ types: [], categories: [], subcategories: {} });
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [blurred, setBlurred] = useState(false);
  const [shortlist, setShortlist] = useState({}); // id -> product
  const [loading, setLoading] = useState(true);
  const sentinelRef = useRef(null);
  const PAGE_SIZE = 24;
  const [sheetOpen, setSheetOpen] = useState(false);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [options, setOptions] = useState({});
  const [bulk, setBulk] = useState({ quantity: 1, karat: "22kt", ring_size: "" });
  const [mySelOpen, setMySelOpen] = useState(false);
  const [mySelections, setMySelections] = useState([]);
  const [aiSearching, setAiSearching] = useState(false);
  const [aiWeight, setAiWeight] = useState({ min: null, max: null });

  useEffect(() => {
    api.get("/catalog/categories").then((c) => setCategories(c.data)).catch(() => {});
    api.get("/taxonomy").then((t) => setTaxonomy(t.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchPage = useCallback(async (nextPage, replace) => {
    setLoadingMore(true);
    try {
      const { data } = await api.get("/catalog", {
        params: {
          q: debouncedSearch || undefined,
          category: activeCat === "All" ? undefined : activeCat,
          type: activeType === "All" ? undefined : activeType,
          sub_category: activeSub === "All" ? undefined : activeSub,
          min_gross: aiWeight.min ?? undefined,
          max_gross: aiWeight.max ?? undefined,
          sort,
          page: nextPage,
          limit: PAGE_SIZE,
        },
      });
      setProducts((prev) => (replace ? data.items : [...prev, ...data.items]));
      setHasMore(data.has_more);
      setTotal(data.total);
      setPage(data.page);
    } catch {
      toast.error("Failed to load your catalog");
    } finally {
      setLoadingMore(false);
      setLoading(false);
    }
  }, [debouncedSearch, activeCat, activeType, activeSub, sort, aiWeight]);

  useEffect(() => {
    setLoading(true);
    fetchPage(1, true);
  }, [fetchPage]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loadingMore) {
        fetchPage(page + 1, false);
      }
    }, { rootMargin: "400px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loadingMore, page, fetchPage]);

  useEffect(() => {
    const noMenu = (e) => {
      if (e.target && e.target.tagName === "IMG") e.preventDefault();
    };
    const onVis = () => setBlurred(document.hidden);
    const onBlur = () => setBlurred(true);
    const onFocus = () => { setBlurred(false); api.get("/taxonomy").then((t) => setTaxonomy(t.data)).catch(() => {}); };
    document.addEventListener("contextmenu", noMenu);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("contextmenu", noMenu);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const shortlistItems = Object.values(shortlist);
  const filtered = products;

  const runAiSearch = async () => {
    const query = search.trim();
    if (!query) { toast.message("Type what you're looking for, then tap Ask AI"); return; }
    setAiSearching(true);
    try {
      const { data } = await api.post("/ai/parse-search", { query });
      const types = taxonomy.types || [];
      const cats = taxonomy.categories || [];
      const nextType = data.type && types.includes(data.type) ? data.type : "All";
      const nextCat = data.category && cats.includes(data.category) ? data.category : "All";
      const subs = taxonomy.subcategories?.[nextCat] || [];
      const nextSub = data.sub_category && subs.includes(data.sub_category) ? data.sub_category : "All";
      setActiveType(nextType); setActiveCat(nextCat); setActiveSub(nextSub);
      if (["latest", "best_seller", "weight_desc", "weight_asc"].includes(data.sort)) setSort(data.sort);
      setAiWeight({ min: data.min_gross ?? null, max: data.max_gross ?? null });
      setSearch(typeof data.q === "string" ? data.q : "");
      toast.success("Filters applied from your request");
    } catch {
      toast.error("Couldn't interpret that. Try rephrasing.");
    } finally {
      setAiSearching(false);
    }
  };

  const toggle = (product) => {
    setShortlist((prev) => {
      const next = { ...prev };
      if (next[product.id]) delete next[product.id];
      else next[product.id] = product;
      return next;
    });
    setOptions((prev) => {
      const next = { ...prev };
      if (next[product.id]) delete next[product.id];
      else next[product.id] = { quantity: 1, karat: "22kt", ring_size: "" };
      return next;
    });
  };

  const setOption = (id, key, value) =>
    setOptions((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || { quantity: 1, karat: "22kt", ring_size: "" }), [key]: value },
    }));

  const applyToAll = () => {
    setOptions((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((id) => {
        const item = shortlist[id];
        next[id] = {
          quantity: bulk.quantity,
          karat: bulk.karat,
          ring_size: item && item.category === "Rings" ? bulk.ring_size : next[id].ring_size,
        };
      });
      return next;
    });
    toast.success("Applied to all items");
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const generatePdf = (ref) => {
    const doc = new jsPDF();
    doc.setFont("times", "normal");
    doc.setFontSize(22);
    doc.text("ADASTRA", 14, 20);
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text("Selection Sheet", 14, 27);
    doc.setTextColor(0);
    doc.text(`Reference: ${ref}`, 14, 37);
    doc.text(`Buyer: ${user.name} (${user.email})`, 14, 43);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 14, 49);
    if (note) doc.text(`Note: ${note}`, 14, 55);
    autoTable(doc, {
      startY: note ? 60 : 54,
      head: [["SKU", "Item", "Category", "Net Wt", "Gross Wt", "Stone Wt", "Qty", "Karat", "Size"]],
      body: shortlistItems.map((it) => {
        const o = options[it.id] || { quantity: 1, karat: "", ring_size: "" };
        return [it.sku, it.name, it.category, `${it.net_weight}g`, `${it.gross_weight}g`, `${it.stone_weight}g`, o.quantity, o.karat, o.ring_size || "-"];
      }),
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [10, 10, 11] },
    });
    doc.save(`Adastra-Selection-${ref}.pdf`);
  };

  const generatePdfFromSelection = (sel) => {
    const doc = new jsPDF();
    doc.setFont("times", "normal");
    doc.setFontSize(22);
    doc.text("ADASTRA", 14, 20);
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text("Selection Sheet", 14, 27);
    doc.setTextColor(0);
    doc.text(`Reference: ${sel.ref}`, 14, 37);
    doc.text(`Buyer: ${sel.buyer_name} (${sel.buyer_email})`, 14, 43);
    doc.text(`Date: ${new Date(sel.created_at).toLocaleDateString()}`, 14, 49);
    if (sel.note) doc.text(`Note: ${sel.note}`, 14, 55);
    autoTable(doc, {
      startY: sel.note ? 60 : 54,
      head: [["SKU", "Item", "Category", "Net Wt", "Gross Wt", "Stone Wt", "Qty", "Karat", "Size"]],
      body: (sel.items || []).map((it) => [
        it.sku, it.name, it.category, `${it.net_weight}g`, `${it.gross_weight}g`, `${it.stone_weight}g`,
        it.quantity || 1, it.karat || "", it.ring_size || "-",
      ]),
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [10, 10, 11] },
    });
    doc.save(`Adastra-Selection-${sel.ref}.pdf`);
  };

  const openMySelections = async () => {
    setMySelOpen(true);
    try {
      const { data } = await api.get("/selections/mine");
      setMySelections(data);
    } catch {
      toast.error("Failed to load your selections");
    }
  };

  const submitSheet = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/selections", {
        items: shortlistItems.map((i) => ({
          product_id: i.id,
          ...(options[i.id] || { quantity: 1, karat: "22kt", ring_size: "" }),
        })),
        note,
      });
      generatePdf(data.ref);
      toast.success(`Selection ${data.ref} submitted — a copy was emailed to you.`);
      setShortlist({});
      setOptions({});
      setNote("");
      setSheetOpen(false);
    } catch {
      toast.error("Failed to submit selection sheet");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <WhatsAppFab />
      <ConciergeChat />
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-black/5">
        <div className="mx-auto max-w-7xl px-6 min-h-20 py-3 flex items-center justify-between">
          <div>
            <div className="brand-mark text-xl" data-testid="brand-logo">ADASTRA</div>
            <p className="overline text-foreground mt-0.5" data-testid="welcome-text">Welcome, {user.name}</p>
            <p className="overline text-muted-foreground mt-0.5" data-testid="private-catalog-label">Private Catalog</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <Button
              variant="ghost"
              className="rounded-sm h-11"
              onClick={openMySelections}
              data-testid="my-selections-button"
            >
              <History className="h-4 w-4 sm:mr-2" strokeWidth={1.5} aria-hidden="true" />
              <span className="hidden sm:inline">My Selections</span>
            </Button>
            <Button
              variant="outline"
              className="rounded-sm border-border relative h-11"
              onClick={() => setSheetOpen(true)}
              disabled={shortlistItems.length === 0}
              data-testid="open-selection-sheet-button"
            >
              <ShoppingBag className="h-4 w-4 sm:mr-2" strokeWidth={1.5} aria-hidden="true" />
              <span className="hidden sm:inline">Selection Sheet</span>
              {shortlistItems.length > 0 && (
                <span
                  className="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-foreground px-1.5 text-xs text-background"
                  data-testid="shortlist-count"
                >
                  {shortlistItems.length}
                </span>
              )}
            </Button>
            <Button variant="ghost" className="rounded-sm h-11" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="h-4 w-4 sm:mr-2" strokeWidth={1.5} aria-hidden="true" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <Dialog open={mySelOpen} onOpenChange={setMySelOpen}>
        <DialogContent className="max-w-2xl rounded-sm" data-testid="my-selections-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl tracking-tight">My Selections</DialogTitle>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto">
            {mySelections.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center" data-testid="my-selections-empty">
                You haven't submitted any selection sheets yet.
              </p>
            ) : (
              <div className="divide-y divide-border">
                {mySelections.map((s) => (
                  <div key={s.id} className="flex items-center gap-4 py-4" data-testid={`my-selection-${s.ref}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{s.ref}</p>
                      <p className="overline text-muted-foreground mt-0.5">
                        {s.items?.length} items · {new Date(s.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge variant="outline" className="rounded-sm capitalize hidden sm:inline-flex">{s.status}</Badge>
                    <Button variant="outline" size="sm" className="rounded-sm" onClick={() => generatePdfFromSelection(s)} data-testid={`redownload-${s.ref}`}>
                      <FileDown className="h-4 w-4 mr-2" strokeWidth={1.5} aria-hidden="true" />
                      PDF
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {blurred && (
        <div className="fixed inset-0 z-[60] bg-background/95 backdrop-blur-xl flex items-center justify-center" data-testid="privacy-overlay">
          <div className="text-center px-6">
            <div className="brand-mark text-2xl mb-3">ADASTRA</div>
            <p className="text-sm text-muted-foreground">Content hidden while this window is inactive.</p>
          </div>
        </div>
      )}

      <main className={`mx-auto max-w-7xl px-6 py-10 ${blurred ? "catalog-blur" : ""}`}>
        <div className="mb-10">
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Your Collection</h1>
        </div>

        {/* Three-level navigation + sort */}
        <div className="mb-10 border-b border-border pb-5 space-y-4" data-testid="catalog-nav">
          <div className="flex flex-col lg:flex-row lg:items-start gap-4">
            <div className="flex-1 space-y-3">
              <NavRow
                label="Type"
                testid="nav-type"
                options={["All", ...(taxonomy.types || [])]}
                active={activeType}
                onSelect={(v) => { setActiveType(v); setActiveCat("All"); setActiveSub("All"); }}
              />
              {activeType !== "All" && (
                <NavRow
                  label="Category"
                  testid="nav-category"
                  options={["All", ...(taxonomy.categories || [])]}
                  active={activeCat}
                  onSelect={(v) => { setActiveCat(v); setActiveSub("All"); }}
                />
              )}
              {activeCat !== "All" && (taxonomy.subcategories?.[activeCat] || []).length > 0 && (
                <NavRow
                  label="Sub-category"
                  testid="nav-subcategory"
                  options={["All", ...(taxonomy.subcategories[activeCat] || [])]}
                  active={activeSub}
                  onSelect={setActiveSub}
                />
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 lg:w-auto">
              <div className="relative sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} aria-hidden="true" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or SKU"
                  className="h-10 rounded-sm pl-9"
                  data-testid="catalog-search-input"
                />
              </div>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="h-10 rounded-sm sm:w-52" data-testid="catalog-sort-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Latest</SelectItem>
                  <SelectItem value="best_seller">Best Seller</SelectItem>
                  <SelectItem value="weight_desc">Gram Weight: High to Low</SelectItem>
                  <SelectItem value="weight_asc">Gram Weight: Low to High</SelectItem>
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="outline"
                onClick={runAiSearch}
                disabled={aiSearching}
                className="h-10 rounded-sm gap-2"
                data-testid="ai-search-button"
              >
                {aiSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />}
                Ask AI
              </Button>
            </div>
          </div>
          {(aiWeight.min != null || aiWeight.max != null) && (
            <div className="flex items-center gap-2" data-testid="ai-weight-filter">
              <Badge variant="outline" className="rounded-sm">
                Gross weight {aiWeight.min != null ? `≥ ${aiWeight.min}g` : ""}{aiWeight.min != null && aiWeight.max != null ? " · " : ""}{aiWeight.max != null ? `≤ ${aiWeight.max}g` : ""}
              </Badge>
              <button onClick={() => setAiWeight({ min: null, max: null })} className="text-xs text-muted-foreground underline" data-testid="ai-weight-clear">Clear</button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-square bg-[#F7F7F8] animate-pulse rounded-sm" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-24 text-center" data-testid="empty-catalog">
            <p className="font-serif text-2xl mb-2">No items assigned yet</p>
            <p className="text-sm text-muted-foreground">
              Your account manager will assign products to your catalog shortly.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12" data-testid="product-grid">
            {filtered.map((p, idx) => {
              const selected = !!shortlist[p.id];
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: Math.min(idx * 0.04, 0.4) }}
                  className="group"
                  data-testid={`product-card-${p.sku}`}
                >
                  <div className="relative overflow-hidden bg-[#F7F7F8] rounded-sm aspect-square">
                    <img
                      src={p.image_url || IMG_PLACEHOLDER}
                      alt={p.name}
                      draggable={false}
                      onContextMenu={(e) => e.preventDefault()}
                      className="no-save-img h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                    />
                    <button
                      onClick={() => toggle(p)}
                      data-testid={`shortlist-toggle-${p.sku}`}
                      className={`absolute top-3 right-3 h-10 w-10 flex items-center justify-center rounded-full backdrop-blur-md transition-colors ${
                        selected ? "bg-foreground text-background" : "bg-white/80 text-foreground hover:bg-white"
                      }`}
                      aria-label={selected ? "Remove from shortlist" : "Add to shortlist"}
                    >
                      <Heart className="h-4 w-4" strokeWidth={1.5} fill={selected ? "currentColor" : "none"} aria-hidden="true" />
                    </button>
                  </div>
                  <div className="mt-4">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-serif text-xl leading-tight tracking-tight">{p.name}</h3>
                    </div>
                    <p className="overline text-muted-foreground mt-1">{p.sku}</p>
                    <div className="grid grid-cols-3 gap-1 mt-3 text-center border-t border-border pt-3">
                      <Weight label="Net" value={p.net_weight} />
                      <Weight label="Gross" value={p.gross_weight} />
                      <Weight label="Stone" value={p.stone_weight} />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="mt-12 flex flex-col items-center gap-3">
            <div ref={sentinelRef} className="h-1 w-full" />
            {loadingMore && <p className="overline text-muted-foreground" data-testid="loading-more">Loading more…</p>}
            {!hasMore && <p className="overline text-muted-foreground" data-testid="catalog-count">Showing all {total} pieces</p>}
            {hasMore && !loadingMore && (
              <Button variant="outline" className="rounded-sm" onClick={() => fetchPage(page + 1, false)} data-testid="load-more-button">Load more</Button>
            )}
          </div>
        )}
      </main>

      {/* Selection Sheet Dialog */}
      <Dialog open={sheetOpen} onOpenChange={setSheetOpen}>
        <DialogContent className="max-w-2xl w-[calc(100vw-2rem)] rounded-sm max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="selection-sheet-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl tracking-tight">Selection Sheet</DialogTitle>
          </DialogHeader>
          {shortlistItems.length > 0 && (
            <div className="border border-border rounded-sm p-3 bg-secondary/50" data-testid="bulk-controls">
              <p className="overline text-muted-foreground mb-2">Apply to all items</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="overline text-muted-foreground" style={{ fontSize: "0.6rem" }}>Qty</label>
                  <Input type="number" min={1} value={bulk.quantity}
                    onChange={(e) => setBulk({ ...bulk, quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                    className="h-9 rounded-sm mt-1" data-testid="bulk-qty-input" />
                </div>
                <div>
                  <label className="overline text-muted-foreground" style={{ fontSize: "0.6rem" }}>Karat</label>
                  <Select value={bulk.karat} onValueChange={(v) => setBulk({ ...bulk, karat: v })}>
                    <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="bulk-karat-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{KARAT_OPTIONS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="overline text-muted-foreground" style={{ fontSize: "0.6rem" }}>Ring Size</label>
                  <Input value={bulk.ring_size} onChange={(e) => setBulk({ ...bulk, ring_size: e.target.value })}
                    className="h-9 rounded-sm mt-1" data-testid="bulk-size-input" />
                </div>
              </div>
              <Button variant="outline" size="sm" className="rounded-sm mt-3" onClick={applyToAll} data-testid="apply-to-all-button">
                Apply to all
              </Button>
            </div>
          )}
          <div className="max-h-[45vh] overflow-y-auto overflow-x-hidden">
            {shortlistItems.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Your shortlist is empty.</p>
            ) : (
              <div className="divide-y divide-border">
                {shortlistItems.map((it) => {
                  const opt = options[it.id] || { quantity: 1, karat: "22kt", ring_size: "" };
                  const isRing = it.category === "Rings";
                  return (
                    <div key={it.id} className="py-4" data-testid={`sheet-item-${it.sku}`}>
                      <div className="flex items-center gap-4">
                        <div className="h-14 w-14 shrink-0 bg-[#F7F7F8] rounded-sm overflow-hidden">
                          <img src={it.image_url} alt={it.name} className="h-full w-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-serif text-lg leading-tight truncate">{it.name}</p>
                          <p className="overline text-muted-foreground">{it.sku} · {it.category}</p>
                        </div>
                        <button
                          onClick={() => toggle(it)}
                          className="text-muted-foreground hover:text-destructive transition-colors"
                          data-testid={`sheet-remove-${it.sku}`}
                          aria-label="Remove"
                        >
                          <X className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                        </button>
                      </div>
                      <div className="grid grid-cols-3 gap-3 mt-3 sm:pl-[4.5rem]">
                        <div>
                          <label className="overline text-muted-foreground" style={{ fontSize: "0.6rem" }}>Qty</label>
                          <Input type="number" min={1} value={opt.quantity}
                            onChange={(e) => setOption(it.id, "quantity", Math.max(1, parseInt(e.target.value) || 1))}
                            className="h-9 rounded-sm mt-1" data-testid={`item-qty-${it.sku}`} />
                        </div>
                        <div>
                          <label className="overline text-muted-foreground" style={{ fontSize: "0.6rem" }}>Karat</label>
                          <Select value={opt.karat} onValueChange={(v) => setOption(it.id, "karat", v)}>
                            <SelectTrigger className="h-9 rounded-sm mt-1" data-testid={`item-karat-${it.sku}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{KARAT_OPTIONS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="overline text-muted-foreground" style={{ fontSize: "0.6rem" }}>Ring Size</label>
                          <Input value={opt.ring_size} disabled={!isRing} placeholder={isRing ? "e.g. 12" : "—"}
                            onChange={(e) => setOption(it.id, "ring_size", e.target.value)}
                            className="h-9 rounded-sm mt-1" data-testid={`item-size-${it.sku}`} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="pt-2">
            <label className="overline text-foreground">Note to Adastra (optional)</label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Sizes, quantities, or special requests…"
              className="mt-2 rounded-sm"
              data-testid="selection-note-input"
            />
          </div>
          <DialogFooter className="flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-2">
            <Button
              variant="outline"
              className="rounded-sm"
              disabled={shortlistItems.length === 0}
              onClick={() => generatePdf("DRAFT")}
              data-testid="download-pdf-button"
            >
              <FileDown className="h-4 w-4 mr-2" strokeWidth={1.5} aria-hidden="true" />
              Download PDF
            </Button>
            <Button
              className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
              disabled={shortlistItems.length === 0 || submitting}
              onClick={submitSheet}
              data-testid="submit-selection-button"
            >
              <Check className="h-4 w-4 mr-2" strokeWidth={1.5} aria-hidden="true" />
              {submitting ? "Submitting…" : "Submit Selection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const NavRow = ({ label, options, active, onSelect, testid }) => (
  <div className="flex flex-col sm:flex-row sm:items-start gap-2" data-testid={testid}>
    <span className="overline text-muted-foreground sm:w-28 shrink-0 sm:pt-2">{label}</span>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o}
          onClick={() => onSelect(o)}
          data-testid={`${testid}-${String(o).toLowerCase().replace(/\s+/g, "-")}`}
          className={`overline px-3 py-1.5 rounded-sm border transition-colors ${
            active === o
              ? "bg-foreground text-background border-foreground"
              : "border-border text-muted-foreground hover:text-foreground hover:bg-secondary"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  </div>
);

const Weight = ({ label, value }) => (
  <div>
    <p className="overline text-muted-foreground" style={{ fontSize: "0.6rem" }}>{label}</p>
    <p className="text-sm font-medium mt-0.5">{value}g</p>
  </div>
);
